import { ReactNode } from 'react';
import SimpleForm, { SimpleFormItemProps } from '../../SimpleForm';
import { EditColumnType } from '../interface';
import { dataToUpload, dataToUploads } from '../../EdiTable/util';
import dayjs from 'dayjs';

function getFormItem(record: any, props?: EditColumnType): ReactNode {
  if (!props) return null;
  const {
    formItemType,
    name,
    initialValue,
    required,
    rules,
    form,
    disabledRender,
    disabledRenderKey,
    enumCode,
    style,
    disabled,
    relativePathName,
    afterFormItemProps,
    inputProps,
    inputNumberProps,
    switchProps,
    checkboxProps,
    checkboxGroupProps,
    datePickerProps,
    rangePickerProps,
    timePickerProps,
    textAreaProps,
    selectProps,
    selectTableProps,
    superSelectProps,
    superSelectTableProps,
    uploadProps,
    uploadImageProps,
    uploadImagesProps,
    beforeSuperSelectProps,
    afterSuperSelectProps,
    beforeFormItemProps,
    formatRules,
    optionFieldName,
    optionFieldNames,
  } = props;
  const relativePath = relativePathName
    ? record[relativePathName]
    : initialValue;
  const baseSearchFormProps: SimpleFormItemProps = {
    name,
    initialValue,
    form,
    rules: formatRules ? formatRules(record) : rules,
  };
  if (disabledRenderKey && !disabledRender) {
    baseSearchFormProps.disabledRender = record[disabledRenderKey];
  }
  if (required && !rules) {
    baseSearchFormProps.rules = [
      {
        required,
      },
    ];
  }
  const baseFormProps = { disabled: typeof disabled === 'function' ? disabled(record) : disabled, };
  switch (formItemType) {
    case 'Input':
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{
            ...baseFormProps,
            ...inputProps,
            onChange: inputProps?.onChange ? (value, formData, form) => {
              inputProps?.onChange?.(value, { ...record, ...formData }, form);
            } : undefined,
          }}
        />
      );
    case 'TextArea':
      return (
        <SimpleForm.TextArea
          {...baseSearchFormProps}
          textAreaProps={{ ...baseFormProps, ...textAreaProps }}
        />
      );
    case 'InputNumber':
      return (
        <SimpleForm.InputNumber
          {...baseSearchFormProps}
          inputNumberProps={{
            ...baseFormProps,
            ...inputNumberProps,
            onChange: inputNumberProps?.onChange ? (value, formData, form) => {
              inputNumberProps?.onChange?.(value, { ...record, ...formData }, form);
            } : undefined,
          }}
        />
      );
    case 'Switch':
      if (baseSearchFormProps.initialValue === undefined) {
        baseSearchFormProps.initialValue = false;
      }
      return (
        <SimpleForm.Switch
          {...baseSearchFormProps}
          switchProps={{ ...baseFormProps, ...switchProps }}
        />
      );
    case 'Checkbox':
      return (
        <SimpleForm.Checkbox
          {...baseSearchFormProps}
          checkboxProps={{ ...baseFormProps, ...checkboxProps }}
        />
      );
    case 'CheckboxGroup':
      return (
        <SimpleForm.CheckboxGroup
          {...baseSearchFormProps}
          enumCode={enumCode}
          checkboxGroupProps={{ ...baseFormProps, ...checkboxGroupProps }}
        />
      );
    case 'DatePicker':
      let datePickerValue = baseSearchFormProps.initialValue;
      if (datePickerValue) {
        datePickerValue = dayjs(datePickerValue);
      }
      return (
        <SimpleForm.DatePicker
          {...baseSearchFormProps}
          initialValue={datePickerValue}
          datePickerProps={{
            ...baseFormProps,
            ...datePickerProps,
            onChange: datePickerProps?.onChange ? (date, dateString, formData, form) => {
              datePickerProps?.onChange?.(date, dateString, { ...record, ...formData, }, form);
            } : undefined,
          }}
        />
      );
    case 'RangePicker':
      return (
        <SimpleForm.RangePicker
          {...baseSearchFormProps}
          rangePickerProps={{ ...baseFormProps, ...rangePickerProps }}
        />
      );
    case 'TimePicker':
      return (
        <SimpleForm.TimePicker
          {...baseSearchFormProps}
          timePickerProps={{ ...baseFormProps, ...timePickerProps }}
        />
      );
    case 'Upload':
      return (
        <SimpleForm.Upload
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadProps={{
            size: 'small',
            ...baseFormProps,
            ...uploadProps,
          }}
        />
      );
    case 'UploadImage':
      return (
        <SimpleForm.UploadImage
          className="edit-table-upload-image"
          {...baseSearchFormProps}
          initialValue={dataToUpload(initialValue, relativePath)}
          style={style}
          uploadImageProps={{
            size: 'small',
            ...baseFormProps,
            ...uploadImageProps,
          }}
        />
      );
    case 'UploadImages':
      return (
        <SimpleForm.UploadImages
          className="edit-table-upload-images"
          {...baseSearchFormProps}
          initialValue={dataToUploads(initialValue, relativePath)}
          uploadImagesProps={{
            size: 'small',
            ...baseFormProps,
            ...uploadImagesProps,
          }}
        />
      );
    case 'Select':
      return (
        <SimpleForm.Select
          {...baseSearchFormProps}
          enumCode={enumCode}
          selectProps={{ ...baseFormProps, ...selectProps }}
        />
      );
    case 'SuperSelect':
      return (
        <SimpleForm.SuperSelect
          {...baseSearchFormProps}
          enumCode={enumCode}
          superSelectProps={{
            ...baseFormProps,
            isDisabledEllipsis: true,
            disabledBorderDashed: false,
            ...superSelectProps,
            options: superSelectProps?.options || (optionFieldName ? record?.[optionFieldName] : undefined),
          }}
        />
      );
    case 'SelectTable':
      if (!selectTableProps) return null;
      let initOptions: any[] = [];
      let namekey = props?.namekey;
      let valuekey = props?.valuekey;
      let label = selectTableProps?.nameKey;
      let value = selectTableProps?.valueKey;
      if (namekey && valuekey && value && label) {
        const labels = record[namekey];
        const values = record[valuekey];
        if (Array.isArray(values)) {
          values.forEach((v, k) => {
            initOptions.push({
              [label as string]: labels[k],
              [value as string]: v,
            });
          });
        } else {
          initOptions.push({
            [label]: record[namekey],
            [value]: record[valuekey],
          });
        }
      }
      return (
        <SimpleForm.SelectTable
          {...baseSearchFormProps}
          selectTableProps={{
            initOptions,
            ...baseFormProps,
            ...selectTableProps,
            // @ts-ignore
            onChange: (value: any, item: any, index?: number) => {
              // @ts-ignore
              selectTableProps.onChange?.(value, item, index, record,);
            },
            otherParams: typeof selectTableProps.otherParams === 'function' ? selectTableProps.otherParams(record) : selectTableProps.otherParams,
          }}
        />
      );
    case 'SuperSelectTable':
      if (!superSelectTableProps) return null;
      let initOptions_1: any[] = [];
      let labelKey_1 = props?.labelKey;
      let valueKey_1 = props?.valueKey || (Array.isArray(name) ? name[0] : name);
      let label_1 = superSelectTableProps?.labelKey;
      let value_1 = superSelectTableProps?.valueKey;
      if (labelKey_1 && valueKey_1 && label_1 && value_1) {
        const labels = record[labelKey_1];
        const values = record[valueKey_1];
        if (labels !== undefined && values !== undefined) {
          if (Array.isArray(values)) {
            values.forEach((v, k) => {
              initOptions_1.push({
                [label_1 as string]: labels[k],
                [value_1 as string]: v,
              });
            });
          } else {
            initOptions_1.push({
              [label_1]: record[labelKey_1],
              [value_1]: record[valueKey_1],
            });
          }
        }
      }
      return (
        <SimpleForm.SuperSelectTable
          {...baseSearchFormProps}
          superSelectTableProps={{
            disabledBorderDashed: false,
            initOptions: initOptions_1.length ? initOptions_1 : undefined,
            ...baseFormProps,
            ...superSelectTableProps,
            // @ts-ignore
            onChange: (value: any, item: any, index?: number) => {
              // @ts-ignore
              superSelectTableProps.onChange?.(value, item, index, record,);
            },
            otherParams: typeof superSelectTableProps.otherParams === 'function' ? superSelectTableProps.otherParams(record) : superSelectTableProps.otherParams,
          }}
        />
      );
    case 'SelectCombination':
      let beforeOptionFieldName, afterOptionFieldName;
      if (Array.isArray(optionFieldNames)) {
        beforeOptionFieldName = optionFieldNames[0];
        afterOptionFieldName = optionFieldNames[1];
      }
      return (
        <SimpleForm.SelectCombination
          {...baseSearchFormProps}
          beforeFormItemProps={{...beforeFormItemProps, initialValue: baseSearchFormProps.initialValue?.[0]}}
          afterFormItemProps={{...afterFormItemProps, initialValue: baseSearchFormProps.initialValue?.[1]}}
          beforeSuperSelectProps={{
            ...baseFormProps,
            ...beforeSuperSelectProps,
            options: beforeSuperSelectProps?.options || (beforeOptionFieldName ? record?.[beforeOptionFieldName] : undefined),
          }}
          afterSuperSelectProps={{
            ...baseFormProps,
            ...afterSuperSelectProps,
            options: afterSuperSelectProps?.options || (afterOptionFieldName ? record?.[afterOptionFieldName] : undefined),
          }}
        />
      );
    default:
      return (
        <SimpleForm.Input
          {...baseSearchFormProps}
          inputProps={{ ...baseFormProps, ...inputProps }}
        />
      );
  }
}

export default getFormItem;
